package com.sweetlime.bingeamigo.presentation.navigation

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.bingeamigo.DeepLinkHandler
import com.sweetlime.bingeamigo.model.Episode
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.LibraryItem
import com.sweetlime.bingeamigo.model.Movie
import com.sweetlime.bingeamigo.model.SearchResultItem
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.model.toLibraryItem
import com.sweetlime.bingeamigo.model.toSearchResultItem
import com.sweetlime.bingeamigo.presentation.features.tvdetail.EpisodeDetailScreen
import com.sweetlime.bingeamigo.presentation.features.home.HomeScreen
import com.sweetlime.bingeamigo.presentation.features.library.LibraryScreen
import com.sweetlime.bingeamigo.presentation.features.library.LibraryViewModel
import com.sweetlime.bingeamigo.presentation.features.library.TvLibraryViewModel
import com.sweetlime.bingeamigo.presentation.features.moviedetail.MovieDetailScreen
import com.sweetlime.bingeamigo.model.types.FollowType
import com.sweetlime.bingeamigo.presentation.features.follow.FollowListScreen
import com.sweetlime.bingeamigo.presentation.features.profile.ProfileContent
import com.sweetlime.bingeamigo.presentation.features.profile.ProfileViewModel
import com.sweetlime.bingeamigo.presentation.features.search.SearchScreen
import com.sweetlime.bingeamigo.presentation.features.search.SearchViewModel
import com.sweetlime.bingeamigo.presentation.features.search.TvShowSearchViewModel
import com.sweetlime.bingeamigo.presentation.features.search.UserSearchViewModel
import com.sweetlime.bingeamigo.presentation.features.settings.SettingsScreen
import com.sweetlime.bingeamigo.presentation.features.tvdetail.TvShowDetailScreen
import com.sweetlime.bingeamigo.presentation.features.user.UserProfileScreen
import com.sweetlime.bingeamigo.presentation.features.xplog.XpLogScreen
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import com.sweetlime.bingeamigo.state.MovieState
import com.sweetlime.bingeamigo.state.TvShowState
import kotlinx.coroutines.delay
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

object MainScreen : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val deepLink by DeepLinkHandler.pending.collectAsState()
        LaunchedEffect(deepLink) {
            val link = deepLink ?: return@LaunchedEffect
            DeepLinkHandler.consume()
            navigator.push(TvShowDetailScreen(tvShowId = link.showId, tvShowName = link.showName))
        }

        var selectedTab by rememberSaveable { mutableStateOf(HomeTab.Home) }

        Scaffold(
            containerColor = BrandColors.Background,
            bottomBar = {
                BingeAmigoBottomBar(
                    selected = selectedTab,
                    onSelect = { selectedTab = it },
                )
            }
        ) { padding ->
            Box(
                Modifier
                    .fillMaxSize()
                    .padding(top = padding.calculateTopPadding())
            ) {
                AnimatedContent(
                    targetState = selectedTab,
                    transitionSpec = { fadeIn() togetherWith fadeOut() }
                ) { tab ->
                    when (tab) {
                        HomeTab.Home    -> HomeScreen(
                            onNavigateToSearch = { selectedTab = HomeTab.Search },
                            onNavigateToProfile = { selectedTab = HomeTab.Profile },
                        ).Content()
                        HomeTab.Search  -> SearchTabContent(navigator)
                        HomeTab.Library -> LibraryTabContent(navigator)
                        HomeTab.Profile -> ProfileTabContent(navigator)
                    }
                }
            }
        }
    }
}

@Composable
private fun SearchTabContent(navigator: Navigator) {
    val movieViewModel = koinViewModel<SearchViewModel>()
    val tvViewModel = koinViewModel<TvShowSearchViewModel>()
    val userViewModel = koinViewModel<UserSearchViewModel>()

    val movieState by movieViewModel.movies.collectAsState()
    val tvState by tvViewModel.shows.collectAsState()
    val users by userViewModel.users.collectAsState()
    val followedUserIds by userViewModel.followedUserIds.collectAsState()
    val isUserLoading by userViewModel.isLoading.collectAsState()

    var query by rememberSaveable { mutableStateOf("") }

    LaunchedEffect(query) {
        if (query.length >= 3) {
            delay(400)
            movieViewModel.searchMovies(query)
            tvViewModel.searchTvShows(query)
            userViewModel.searchUsers(query)
        } else if (query.isBlank()) {
            movieViewModel.loadPopularMovies()
            tvViewModel.loadPopularTvShows()
            userViewModel.searchUsers("")
        }
    }

    val movieResults = (movieState as? MovieState.Success)?.movies?.map { it.toSearchResultItem() } ?: emptyList()
    val tvResults = (tvState as? TvShowState.Success)?.shows?.map { it.toSearchResultItem() } ?: emptyList()

    SearchScreen(
        query = query,
        onQueryChange = { query = it },
        movieResults = movieResults,
        tvResults = tvResults,
        users = users,
        isUserLoading = isUserLoading,
        followedUserIds = followedUserIds,
        onFollow = { userViewModel.followUser(it) },
        onUnfollow = { userViewModel.unfollowUser(it) },
        onOpenUserProfile = { navigator.push(UserProfileScreen(it)) },
        onMovieClick = { item ->
            val movie = (movieState as? MovieState.Success)?.movies?.firstOrNull { it.id == item.id }
            if (movie != null) navigator.push(MovieDetailScreen(movie))
        },
        onTvClick = { item ->
            val show = (tvState as? TvShowState.Success)?.shows?.firstOrNull { it.id == item.id }
            if (show != null) navigator.push(TvShowDetailScreen(show))
        },
    )
}

@Composable
private fun LibraryTabContent(navigator: Navigator) {
    val movieViewModel = koinViewModel<LibraryViewModel>()
    val tvViewModel = koinViewModel<TvLibraryViewModel>()

    val movieWatchlist by movieViewModel.watchlist.collectAsState()
    val movieWatched by movieViewModel.watched.collectAsState()
    val movieFavorites by movieViewModel.favorites.collectAsState()
    val tvTracking by tvViewModel.tracking.collectAsState()
    val tvCompleted by tvViewModel.completed.collectAsState()
    val tvFavorites by tvViewModel.favorites.collectAsState()

    LibraryScreen(
        movieWatchlist = movieWatchlist.map { it.toLibraryItem() },
        movieWatched = movieWatched.map { it.toLibraryItem() },
        movieFavorites = movieFavorites.map { it.toLibraryItem() },
        tvTracking = tvTracking.map { it.toLibraryItem() },
        tvCompleted = tvCompleted.map { it.toLibraryItem() },
        tvFavorites = tvFavorites.map { it.toLibraryItem() },
        onItemClick = { item, mediaType ->
            if (mediaType == "Movies") {
                val src = (movieWatchlist + movieWatched + movieFavorites).firstOrNull { it.id == item.id }
                src?.let { navigator.push(MovieDetailScreen(it.movie)) }
            } else {
                val src = (tvTracking + tvCompleted + tvFavorites).firstOrNull { it.id == item.id }
                src?.let { navigator.push(TvShowDetailScreen(it.tvShow)) }
            }
        },
        onRemove = { item, mediaType, collection ->
            when {
                mediaType == "Movies" && collection == "Watchlist" ->
                    movieWatchlist.firstOrNull { it.id == item.id }?.let { movieViewModel.removeFromWatchlist(it) }
                mediaType == "Movies" && collection == "Watched" ->
                    movieWatched.firstOrNull { it.id == item.id }?.let { movieViewModel.removeFromWatched(it) }
                mediaType == "Movies" && collection == "Favorites" ->
                    movieFavorites.firstOrNull { it.id == item.id }?.let { movieViewModel.removeFromFavorites(it) }
                mediaType == "TV Shows" && collection == "Tracking" ->
                    tvTracking.firstOrNull { it.id == item.id }?.let { tvViewModel.removeFromTracking(it) }
                mediaType == "TV Shows" && collection == "Completed" ->
                    tvCompleted.firstOrNull { it.id == item.id }?.let { tvViewModel.removeFromCompleted(it) }
                mediaType == "TV Shows" && collection == "Favorites" ->
                    tvFavorites.firstOrNull { it.id == item.id }?.let { tvViewModel.removeFromFavorites(it) }
            }
        },
    )
}

@Composable
private fun ProfileTabContent(navigator: Navigator) {
    val profileViewModel = koinViewModel<ProfileViewModel>()
    val libraryViewModel: LibraryViewModel = koinInject()

    val profile by profileViewModel.profile.collectAsState()
    val followers by profileViewModel.followers.collectAsState()
    val following by profileViewModel.following.collectAsState()
    val watchlist by libraryViewModel.watchlist.collectAsState()
    val watched by libraryViewModel.watched.collectAsState()
    val favorites by libraryViewModel.favorites.collectAsState()
    val communityRatings by libraryViewModel.communityRatings.collectAsState()
    val activity by profileViewModel.activity.collectAsState()
    val isSavingUsername by profileViewModel.isSavingUsername.collectAsState()
    val usernameError by profileViewModel.usernameError.collectAsState()
    val isSavingAvatar by profileViewModel.isSavingAvatar.collectAsState()

    LaunchedEffect(Unit) {
        profileViewModel.loadProfile()
        profileViewModel.loadFollowers()
        profileViewModel.loadFollowing()
        profileViewModel.loadActivity()
    }

    val xp = profile?.xp ?: 0
    val level = profile?.level ?: 1

    ProfileContent(
        profile = profile,
        email = profileViewModel.userEmail,
        followers = followers.size,
        following = following.size,
        favorites = favorites,
        communityRatings = communityRatings,
        activity = activity,
        isSavingUsername = isSavingUsername,
        usernameError = usernameError,
        isSavingAvatar = isSavingAvatar,
        onOpenSettings = { navigator.push(SettingsScreen) },
        onXpClick = { navigator.push(XpLogScreen(totalXp = xp, level = level)) },
        onFollowersClick = { navigator.push(FollowListScreen(type = FollowType.FOLLOWERS)) },
        onFollowingClick = { navigator.push(FollowListScreen(type = FollowType.FOLLOWING)) },
        onFavoriteClick = { libraryMovie -> navigator.push(MovieDetailScreen(libraryMovie.movie)) },
        onEditUsername = { username, onSuccess -> profileViewModel.updateUsername(username, onSuccess) },
        onUpdateAvatar = { url, onSuccess -> profileViewModel.updateAvatar(url, onSuccess) },
        onActivityClick = { a ->
            val tvShow = if (a.tvShowId != 0)
                TvShow(id = a.tvShowId, name = a.tvShowTitle, overview = "", posterPath = a.tvShowPoster)
            else null
            when {
                a.episodeId != 0 && tvShow != null -> navigator.push(
                    EpisodeDetailScreen(
                        tvShow = tvShow,
                        episode = Episode(
                            id = a.episodeId,
                            name = a.episodeName,
                            episodeNumber = a.episodeNumber,
                            seasonNumber = a.seasonNumber,
                            stillPath = a.episodeStillPath.ifEmpty { null },
                        ),
                        totalEpisodesInShow = 0,
                    )
                )
                tvShow != null -> navigator.push(TvShowDetailScreen(tvShow))
                else -> navigator.push(
                    MovieDetailScreen(
                        Movie(id = a.movieId, title = a.movieTitle, overview = "", posterPath = a.moviePoster)
                    )
                )
            }
        },
    )
}
